/**
 * useLocalizeApiError — the APP-side error-localization seam.
 *
 * `@letsgethome/lib` decodes a caught error to a typed `ServerErrorReason` +
 * metadata (UI-agnostic). This hook owns the UI half: the TYPED reason→catalog
 * key map (`REASON_MESSAGE_KEY`, generated `Record<ServerErrorReason,
 * AllMessageKeys>` — a missing key is a COMPILE error) and the render.
 *
 * Maximum compile-time safety, zero casts:
 *  - the reason key comes from the typed map, the `fallbackKey` param is
 *    `AllMessageKeys` — both validated against the message catalog at compile
 *    time (no free strings reach the renderer);
 *  - rendering goes through `useTranslations().dyn` (the sanctioned `LocalizedText`
 *    mint) with the ALREADY-TYPED key, so there is no `as LocalizedText` cast and
 *    no `t.dyn('<literal>')` (the key is a value, not a literal — see the
 *    `no-dyn-literal-key` rule).
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { decodeServerError, type ServerErrorReason } from '@letsgethome/lib/server-errors';
import type { LocalizedText } from '@letsgethome/types/branded';

import type { AllMessageKeys } from './messages_gen';
import { REASON_MESSAGE_KEY, type PublicServerErrorReason } from './reason_messages_gen';

/** Catalog key for an unmapped reason / non-server error. */
const GENERIC_KEY: AllMessageKeys = 'error.generic';

/** Narrow the full wire reason to the subset the public app localizes — a reason
 *  from another surface (admin) or a forward-compat unknown falls back. */
function isPublicReason(reason: ServerErrorReason): reason is PublicServerErrorReason {
  return reason in REASON_MESSAGE_KEY;
}

export type LocalizeApiError = (err: unknown, fallbackKey?: AllMessageKeys) => LocalizedText;

export function useLocalizeApiError(): LocalizeApiError {
  // Namespaceless translator: `MessageKeys<''>` is the full-key union, so the key
  // is type-checked. `.dyn` allows the runtime ICU `metadata` args (the key is
  // already typed — it comes from the typed map / the typed `fallbackKey`).
  const t = useTranslations();
  return (err, fallbackKey = GENERIC_KEY) => {
    const info = decodeServerError(err);
    const key: AllMessageKeys =
      info.reason && isPublicReason(info.reason) ? REASON_MESSAGE_KEY[info.reason] : fallbackKey;
    return t.dyn(key, info.metadata);
  };
}
